import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowDown, FiCheck, FiZap } = FiIcons;

function CoffeeTamper({ onTampComplete, isActive, grindQuality = 'medium' }) {
  const [tampProgress, setTampProgress] = useState(0);
  const [pressure, setPressure] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [tampQuality, setTampQuality] = useState('none');
  const [showInstructions, setShowInstructions] = useState(true);
  const [holdDuration, setHoldDuration] = useState(0);
  
  const holdTimerRef = useRef(null);
  const pressureTimerRef = useRef(null);

  const targetPressure = 30; // 30 lbs of pressure (standard)
  const holdTimeNeeded = 2000; // 2 seconds hold time

  useEffect(() => {
    if (isActive) {
      setShowInstructions(true);
      const timer = setTimeout(() => setShowInstructions(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  useEffect(() => {
    // Calculate tamp quality based on pressure and hold time
    if (pressure >= 25 && holdDuration >= holdTimeNeeded) {
      setTampQuality('perfect');
    } else if (pressure >= 20 && holdDuration >= 1500) {
      setTampQuality('good');
    } else if (pressure >= 15 && holdDuration >= 1000) {
      setTampQuality('okay');
    } else if (pressure > 0) {
      setTampQuality('poor');
    }
  }, [pressure, holdDuration]);

  const startTamping = () => {
    if (!isActive) return;

    setIsHolding(true);
    
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate([50, 10, 50]);
    }

    // Gradually increase pressure while holding
    pressureTimerRef.current = setInterval(() => {
      setPressure(prev => Math.min(prev + 2, targetPressure + 10));
    }, 100);

    // Track hold duration
    holdTimerRef.current = setInterval(() => {
      setHoldDuration(prev => {
        const newDuration = prev + 100;
        setTampProgress((newDuration / holdTimeNeeded) * 100);
        return newDuration;
      });
    }, 100);
  };

  const stopTamping = () => {
    setIsHolding(false);
    
    // Clear timers
    if (pressureTimerRef.current) {
      clearInterval(pressureTimerRef.current);
    }
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
    }

    // Gradually decrease pressure
    const decreaseTimer = setInterval(() => {
      setPressure(prev => {
        const newPressure = Math.max(prev - 3, 0);
        if (newPressure === 0) {
          clearInterval(decreaseTimer);
        }
        return newPressure;
      });
    }, 50);
  };

  const handleTampComplete = () => {
    if (onTampComplete) {
      onTampComplete({
        quality: tampQuality,
        pressure: pressure,
        holdDuration: holdDuration,
        grindQuality: grindQuality
      });
    }
  };

  const getPressureColor = () => {
    if (pressure >= 25) return 'from-green-400 to-green-600';
    if (pressure >= 20) return 'from-blue-400 to-blue-600';
    if (pressure >= 15) return 'from-yellow-400 to-yellow-600';
    if (pressure >= 10) return 'from-orange-400 to-orange-600';
    return 'from-red-400 to-red-600';
  };

  const getQualityColor = () => {
    const colors = {
      'perfect': 'text-green-500',
      'good': 'text-blue-500',
      'okay': 'text-yellow-500',
      'poor': 'text-red-500',
      'none': 'text-gray-400'
    };
    return colors[tampQuality] || colors.none;
  };

  if (!isActive) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🔨</div>
          <h3 className="text-xl font-bold text-gray-800">Coffee Tamper</h3>
          <p className="text-sm text-gray-600">Hold to apply pressure</p>
        </div>

        {/* Instructions */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="flex items-center text-blue-700">
                <SafeIcon icon={FiArrowDown} className="mr-2 animate-bounce" />
                <span className="text-sm font-medium">
                  Hold down firmly for 2 seconds (30 lbs pressure)
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Portafilter and Tamper Visual */}
        <div className="relative mb-6">
          {/* Portafilter */}
          <div className="w-40 h-24 mx-auto bg-gradient-to-b from-gray-600 to-gray-800 rounded-t-full relative overflow-hidden shadow-xl">
            {/* Coffee grounds */}
            <div className="absolute bottom-0 left-2 right-2 h-8 bg-gradient-to-t from-amber-800 to-amber-600 rounded-t-lg">
              {/* Grounds texture */}
              <div className="absolute inset-0 opacity-30" 
                   style={{
                     backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(139,69,19,0.8) 1px, transparent 1px)',
                     backgroundSize: '4px 4px'
                   }} 
              />
              
              {/* Tamped surface indicator */}
              {tampProgress > 20 && (
                <motion.div
                  className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-900 to-amber-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: tampProgress / 100 }}
                />
              )}
            </div>

            {/* Handle */}
            <div className="absolute -right-6 top-1/2 transform -translate-y-1/2 w-8 h-4 bg-gradient-to-r from-gray-700 to-gray-900 rounded-r-lg" />
          </div>

          {/* Tamper */}
          <motion.div
            className="w-16 h-20 mx-auto bg-gradient-to-b from-gray-300 to-gray-500 rounded-t-full relative cursor-pointer select-none mt-2"
            animate={isHolding ? { y: [0, 8, 0] } : { y: 0 }}
            transition={{ duration: 0.3, repeat: isHolding ? Infinity : 0 }}
            onMouseDown={startTamping}
            onMouseUp={stopTamping}
            onTouchStart={startTamping}
            onTouchEnd={stopTamping}
            whileTap={{ scale: 0.95 }}
          >
            {/* Tamper base */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-4 bg-gradient-to-b from-gray-800 to-gray-900 rounded-full" />
            
            {/* Handle */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-4 h-12 bg-gradient-to-b from-amber-600 to-amber-800 rounded-t-full" />

            {/* Pressure waves */}
            <AnimatePresence>
              {isHolding && pressure > 15 && (
                <motion.div
                  className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  <div className={`w-full h-full bg-gradient-to-r ${getPressureColor()} rounded-full blur-sm`} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Hold instruction */}
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
            <div className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              Hold Down!
            </div>
          </div>
        </div>

        {/* Pressure and Progress Stats */}
        <div className="space-y-4">
          {/* Pressure Gauge */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Pressure Applied</span>
              <span className="font-medium">{Math.round(pressure)} lbs</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <motion.div
                className={`h-4 rounded-full bg-gradient-to-r ${getPressureColor()} relative overflow-hidden`}
                style={{ width: `${Math.min((pressure / (targetPressure + 10)) * 100, 100)}%` }}
                transition={{ duration: 0.2 }}
              >
                {isHolding && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                )}
              </motion.div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span className="font-medium">Target: 30 lbs</span>
              <span>40</span>
            </div>
          </div>

          {/* Hold Duration */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Hold Duration</span>
              <span className="font-medium">{(holdDuration / 1000).toFixed(1)}s</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                style={{ width: `${Math.min(tampProgress, 100)}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Quality Indicator */}
          {tampQuality !== 'none' && (
            <motion.div
              className={`text-center p-3 rounded-lg ${
                tampQuality === 'perfect' ? 'bg-green-100 text-green-700' :
                tampQuality === 'good' ? 'bg-blue-100 text-blue-700' :
                tampQuality === 'okay' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="font-bold capitalize mb-1">
                {tampQuality} Tamp!
              </div>
              <div className="text-xs">
                {tampQuality === 'perfect' && 'Excellent pressure and timing!'}
                {tampQuality === 'good' && 'Good technique, well done!'}
                {tampQuality === 'okay' && 'Decent tamp, could be better.'}
                {tampQuality === 'poor' && 'Needs more pressure or time.'}
              </div>
            </motion.div>
          )}

          {/* Complete Button */}
          {tampProgress >= 80 && tampQuality !== 'none' && (
            <motion.button
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-bold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleTampComplete}
            >
              <SafeIcon icon={FiCheck} className="inline mr-2" />
              Complete Tamp ({tampQuality})
            </motion.button>
          )}

          {/* Grind Quality Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Grind Quality:</span>
              <span className="font-medium capitalize">{grindQuality}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default CoffeeTamper;